import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProductCatalogPicker from "./ProductCatalogPicker";

const INITIAL_FORM = {
  id: "",
  name: "",
  phone: "",
  email: "",
  address: "",
  notes: "",
};

const mapSupplierToForm = (supplier) => ({
  ...INITIAL_FORM,
  id: supplier?.id || supplier?._id || "",
  name: supplier?.name || "",
  phone: supplier?.contact?.phone || "",
  email: supplier?.contact?.email || "",
  address: supplier?.address || "",
  notes: supplier?.notes || "",
});

function SupplierDrawer({
  open,
  onClose,
  onSubmit,
  onUpdate,
  onLinkProducts,
  onDelinkProduct,
  saving = false,
  linking = false,
  error = "",
  linkError = "",
  withOverlay = false,
  supplier = null,
  products = [],
  mode = "create",
}) {
  const drawerRef = useRef(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [initialForm, setInitialForm] = useState(INITIAL_FORM);
  const [localError, setLocalError] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);
  const [saveRequested, setSaveRequested] = useState(false);
  const [sawSavingState, setSawSavingState] = useState(false);
  const isCreateMode = mode === "create";
  const isReadOnly = mode === "view";
  const isEditMode = mode === "edit";
  const allowEdits = !isReadOnly;
  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialForm), [form, initialForm]);
  const normalizeId = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value?.$oid) return value.$oid;
    return value.toString?.() || "";
  }, []);
  const getProductMeta = useCallback((prod = {}) => {
    const base = prod.product || prod;
    const id = normalizeId(base.id || base._id);
    return {
      id: id || "",
      name: base.name || base.title || base?.item?.title || prod.name || "Untitled",
      refCode: base.refCode || prod.refCode || base.sku || prod.sku || "",
      barcode: base.barcode || prod.barcode || "",
      supplierId: base.supplierId || prod.supplierId || "",
      supplierName: base.supplier?.name || prod.supplierName || "",
    };
  }, [normalizeId]);
  const currentSupplierId = useMemo(
    () => normalizeId(supplier?.id || supplier?._id || form.id),
    [form.id, supplier, normalizeId]
  );
  const linkedProductCount = useMemo(() => {
    if (!currentSupplierId || !Array.isArray(products)) return 0;
    return products.reduce((acc, prod) => {
      const meta = getProductMeta?.(prod) || {};
      const supplierId = normalizeId(meta.supplierId);
      return supplierId && supplierId === currentSupplierId ? acc + 1 : acc;
    }, 0);
  }, [currentSupplierId, getProductMeta, normalizeId, products]);

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [field]: value }));
    setLocalError("");
  };

  const validateForm = useCallback(() => {
    if (!form.name.trim()) {
      setLocalError("Supplier name is required.");
      return false;
    }

    const email = form.email.trim();
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setLocalError("Supplier email is invalid.");
      return false;
    }

    return true;
  }, [form]);

  const handleSubmit = useCallback(async () => {
    if (isReadOnly) {
      onClose?.();
      return;
    }
    if (isEditMode && !isDirty) {
      onClose?.();
      return;
    }
    if (saving || saveRequested) return;
    if (!validateForm()) return;

    try {
      const action = isEditMode ? onUpdate : onSubmit;
      if (!action) {
        setLocalError("Unable to save supplier: missing handler.");
        return;
      }
      setLocalError("");
      setSaveRequested(true);
      setSawSavingState(false);
      const pending = isEditMode ? action(form, supplier) : action(form);
      const result = pending?.then ? await pending : pending;
      const succeeded = result !== false;
      setSaveRequested(false);
      setSawSavingState(false);
      if (succeeded) {
        onClose?.();
      }
    } catch (err) {
      setLocalError(err?.message || "Unable to save supplier.");
      setSaveRequested(false);
      setSawSavingState(false);
    }
  }, [form, isDirty, isEditMode, isReadOnly, onClose, onSubmit, onUpdate, supplier, validateForm]);

  useEffect(() => {
    if (saving) {
      setSawSavingState(true);
    }
  }, [saving]);

  const prevLinkingRef = useRef(false);
  useEffect(() => {
    if (prevLinkingRef.current && !linking && !linkError) {
      setSelectedProductIds([]);
      setShowCatalogPicker(false);
    }
    prevLinkingRef.current = linking;
  }, [linkError, linking]);

  useEffect(() => {
    if (!saveRequested) return;
    if (saving) return;
    if (!sawSavingState) return;
    if (localError || error || linkError) {
      setSaveRequested(false);
      setSawSavingState(false);
      return;
    }
    setSaveRequested(false);
    setSawSavingState(false);
    onClose?.();
  }, [error, linkError, localError, onClose, saveRequested, saving, sawSavingState]);

  const handleCloseAttempt = useCallback(() => {
    if (!allowEdits || !isDirty) {
      onClose?.();
      return;
    }

    const shouldSave = window.confirm("You have unsaved changes. Save before closing? Press Cancel to discard.");
    if (shouldSave) {
      handleSubmit();
    } else {
      onClose?.();
    }
  }, [allowEdits, handleSubmit, isDirty, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    if (withOverlay) return undefined;
    const handleClickOutside = (event) => {
      if (!drawerRef.current) return;
      if (drawerRef.current.contains(event.target)) return;
      handleCloseAttempt();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleCloseAttempt, open, withOverlay]);

  useEffect(() => {
    if (open) {
      const nextForm = supplier ? mapSupplierToForm(supplier) : INITIAL_FORM;
      setForm(nextForm);
      setInitialForm(nextForm);
      setLocalError("");
      setSelectedProductIds([]);
      setShowCatalogPicker(false);
      setSaveRequested(false);
      setSawSavingState(false);
    }
  }, [open, supplier]);

  const disableSubmit = saving || !form.name.trim();
  const disableUpdate = disableSubmit;

  const selectionSummary = selectedProductIds.length ? `${selectedProductIds.length} selected from catalog` : "";
  const handleOpenCatalog = () => {
    if (!currentSupplierId) {
      setLocalError("Save supplier before linking products.");
      return;
    }
    setShowCatalogPicker(true);
  };
  const handleCloseCatalog = () => setShowCatalogPicker(false);
  const handleDelinkFromCatalog = useCallback(
    async (ids = []) => {
      if (!onDelinkProduct) return;
      const list = Array.isArray(ids) ? ids : [];
      if (!list.length) return;
      try {
        const pending = onDelinkProduct(list);
        if (pending?.then) {
          await pending;
        }
      } catch (err) {
        setLocalError(err?.message || "Unable to delink products.");
      }
    },
    [onDelinkProduct]
  );
  const handleConfirmCatalogSelection = useCallback(async (ids = []) => {
    const nextIds = Array.isArray(ids) ? ids : [];
    setSelectedProductIds(nextIds);
    if (!currentSupplierId) {
      setLocalError("Save supplier before linking products.");
      return;
    }
    if (!nextIds.length) return;
    const pending = onLinkProducts?.(currentSupplierId, nextIds);
    if (pending?.then) {
      await pending;
    }
  }, [currentSupplierId, onLinkProducts]);

  if (!open) return null;

  const content = (
    <>
      <ProductCatalogPicker
        open={showCatalogPicker}
        onClose={handleCloseCatalog}
        onConfirm={handleConfirmCatalogSelection}
        onDelinkProducts={handleDelinkFromCatalog}
        allowEdits={allowEdits}
        linking={linking}
        products={products}
        selectedIds={selectedProductIds}
        getProductMeta={getProductMeta}
        normalizeId={normalizeId}
        currentSupplierId={currentSupplierId}
      />
      <aside
        className="create-drawer"
        aria-label={isCreateMode ? "Create supplier panel" : "Supplier details panel"}
        ref={drawerRef}
      >
        {localError || error || linkError ? (
          <div className="inline-alert error">
            <span>{localError || error || linkError}</span>
          </div>
        ) : null}

        <div className="drawer-section">
          <h3>Supplier Details</h3>

          <div className="drawer-grid">
            <label className="field">
              <span className="field-label">Supplier name</span>
              <input
                type="text"
                placeholder="Supplier name"
                value={form.name}
                disabled={!allowEdits}
                onChange={handleChange("name")}
              />
            </label>

            <label className="field">
              <span className="field-label">Phone</span>
              <input
                type="tel"
                placeholder="+965 5555 5555"
                value={form.phone}
                disabled={!allowEdits}
                onChange={handleChange("phone")}
              />
            </label>

            <label className="field">
              <span className="field-label">Email</span>
              <input
                type="email"
                placeholder="name@email.com"
                value={form.email}
                disabled={!allowEdits}
                onChange={handleChange("email")}
              />
            </label>

            <label className="field">
              <span className="field-label">Address</span>
              <input
                type="text"
                placeholder="Street, city"
                value={form.address}
                disabled={!allowEdits}
                onChange={handleChange("address")}
              />
            </label>

            <label className="field">
              <span className="field-label">Notes</span>
              <input
                type="text"
                placeholder="Delivery preferences, reminders"
                value={form.notes}
                disabled={!allowEdits}
                onChange={handleChange("notes")}
              />
            </label>
          </div>
        </div>

        <div className="drawer-section products-section">
          <div className="drawer-section__header products-header">
            <h3>Products</h3>
            {currentSupplierId ? <span className="pill status products-count">{linkedProductCount} linked</span> : null}
          </div>
          {currentSupplierId ? (
            <div className="linked-products-layout">
              <div className="linked-card linkable-card">
                <div className="linked-card__head">
                  <div>
                    <div className="field-label">Link from inventory</div>
                    {selectionSummary ? <div className="muted small">{selectionSummary}</div> : null}
                  </div>
                </div>

                <div className="linkable-body">
                  <div className="linkable-actions">
                    <button
                      type="button"
                      className="ghost-btn small"
                      onClick={handleOpenCatalog}
                      disabled={!allowEdits || !currentSupplierId || linking}
                    >
                      Browse catalog
                    </button>
                    <span className="muted extra-small">
                      Opens full catalog with filters and bulk select. Selections sync back here.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="inline-alert info">
              <span>Save the supplier first to link products from inventory.</span>
            </div>
          )}
        </div>

        <footer className="drawer-footer">
          {allowEdits && (isCreateMode || isEditMode) ? (
            <button
              className="primary-btn wide"
              type="button"
              onClick={handleSubmit}
              disabled={isEditMode ? disableUpdate : disableSubmit}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          ) : null}
          <button className="ghost-btn subtle wide" type="button" onClick={onClose} disabled={saving}>
            {isReadOnly ? "Close" : "Cancel"}
          </button>
        </footer>
      </aside>
    </>
  );

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleCloseAttempt();
    }
  };

  if (!withOverlay) return content;

  return (
    <div className="modal-overlay solo-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className="create-layer solo-drawer">{content}</div>
    </div>
  );
}

export default SupplierDrawer;
